"use strict";
var _a, _b, _c, _d, _e;
Object.defineProperty(exports, "__esModule", { value: true });
exports.MeetingRepository = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const constants_1 = require("../../../../../../libs/service-lib/src/lib/constants");
const messages_1 = require("../../../../../../libs/service-lib/src/lib/messages");
const entity_service_utils_1 = require("../../../../../../libs/service-lib/src/lib/utils/entity-service.utils");
const constants_2 = require("../../../../service-lib/src/lib/constants");
const entities_1 = require("../../../../service-lib/src/lib/entities");
let MeetingRepository = class MeetingRepository {
    constructor(meetingRepository, companyRepository, opportunityRepository, opportunityActivityMapRepository, entityService) {
        this.meetingRepository = meetingRepository;
        this.companyRepository = companyRepository;
        this.opportunityRepository = opportunityRepository;
        this.opportunityActivityMapRepository = opportunityActivityMapRepository;
        this.entityService = entityService;
    }
    // Create a new meeting
    async createMeeting(meetingData, manager) {
        try {
            // Meeting linking validation
            await this.validateMeetingAssociations(meetingData.companyId, meetingData.opportunityId, meetingData.activityId);
            // Validate meeting timings
            await this.validateMeetingTimings(meetingData.meetingDate, meetingData.startTime, meetingData.endTime);
            const meeting = manager.create(entities_1.Meeting, meetingData);
            return await manager.save(entities_1.Meeting, meeting);
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            if (error instanceof common_1.BadRequestException)
                throw error;
            throw new Error("Error creating meeting: " + error.message);
        }
    }
    // Update an existing meeting
    async updateMeeting(id, updateMeeting, manager) {
        try {
            const meeting = await manager.findOne(entities_1.Meeting, { where: { id } });
            if (!meeting) {
                throw new common_1.NotFoundException(messages_1.errorMessages.meetingNotFound);
            }
            // Meeting linking validation
            await this.validateMeetingAssociations(updateMeeting.companyId, updateMeeting.opportunityId, updateMeeting.activityId);
            // Validate meeting timings
            await this.validateMeetingTimings(updateMeeting.meetingDate, updateMeeting.startTime, updateMeeting.endTime);
            await manager.update(entities_1.Meeting, id, updateMeeting);
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            if (error instanceof common_1.BadRequestException)
                throw error;
            throw new Error("Error updating meeting: " + error.message);
        }
    }
    // Save meeting participants
    async saveMeetingParticipants(meetingId, participants, manager) {
        try {
            const participantTypeMap = {
                [constants_1.PARTICIPANT_TYPE.TPA_CONTACT]: {
                    entity: entities_1.TpaContact,
                    validate: async (manager, tpaId, contactId) => {
                        const tpaContact = await manager.findOne(entities_1.TpaContact, {
                            where: { id: tpaId, contactId },
                        });
                        if (!tpaContact) {
                            throw new common_1.NotFoundException(`TPA Contact with tpaId ${tpaId} and contactId ${contactId} not found`);
                        }
                    },
                    message: (id) => `TPA Contact with ID ${id} not found`,
                },
                [constants_1.PARTICIPANT_TYPE.INSURER_CONTACT]: {
                    entity: entities_1.InsureContact,
                    validate: async (manager, insurerId, contactId) => {
                        const insurerContact = await manager.findOne(entities_1.InsureContact, {
                            where: { insurerId, contactId },
                        });
                        if (!insurerContact) {
                            throw new common_1.NotFoundException(`Insurer Contact with insurerId ${insurerId} and contactId ${contactId} not found`);
                        }
                    },
                    message: (id) => `Insurer Contact with ID ${id} not found`,
                },
                [constants_1.PARTICIPANT_TYPE.COMPANY_CONTACT]: {
                    entity: entities_1.CompanyContactMap,
                    validate: async (manager, companyId, contactId) => {
                        const companyContact = await manager.findOne(entities_1.CompanyContactMap, {
                            where: { companyId, contactId },
                        });
                        if (!companyContact) {
                            throw new common_1.NotFoundException(`Company Contact with companyId ${companyId} and contactId ${contactId} not found`);
                        }
                    },
                    message: (id) => `Company Contact with ID ${id} not found`,
                },
                [constants_1.PARTICIPANT_TYPE.EMPLOYEE]: {
                    entity: entities_1.User,
                    validate: async (manager, _companyId, employeeId) => {
                        const employee = await manager.findOne(entities_1.User, {
                            where: { userId: employeeId },
                        });
                        if (!employee) {
                            throw new common_1.NotFoundException(`Employee with user ID ${employeeId} not found`);
                        }
                    },
                    message: (id) => `Employee with user ID ${id} not found`,
                },
            };
            // Helper to build participant list from payload
            const normalizedParticipants = [];
            if (participants.tpaParticipants &&
                Object.keys(participants.tpaParticipants).length > 0) {
                const { tpaId, tpaContactPerson } = participants.tpaParticipants;
                if (!tpaId ||
                    !Array.isArray(tpaContactPerson) ||
                    tpaContactPerson.length === 0) {
                    throw new common_1.BadRequestException("TPA participants are required and must be valid.");
                }
                tpaContactPerson.forEach((id) => normalizedParticipants.push({
                    participantId: id,
                    participantCompanyId: tpaId,
                    participantRecordType: constants_1.PARTICIPANT_TYPE.TPA_CONTACT,
                }));
            }
            if (participants.insurerParticipants &&
                Object.keys(participants.insurerParticipants).length > 0) {
                const { insurerId, insurerContactPerson } = participants.insurerParticipants;
                if (!insurerId ||
                    !Array.isArray(insurerContactPerson) ||
                    insurerContactPerson.length === 0) {
                    throw new common_1.BadRequestException("Insurer participants are required and must be valid.");
                }
                insurerContactPerson.forEach((id) => normalizedParticipants.push({
                    participantId: id,
                    participantCompanyId: insurerId,
                    participantRecordType: constants_1.PARTICIPANT_TYPE.INSURER_CONTACT,
                }));
            }
            if (participants.companyParticipants &&
                Object.keys(participants.companyParticipants).length > 0) {
                const { companyId, companyContactPerson } = participants.companyParticipants;
                if (!companyId ||
                    !Array.isArray(companyContactPerson) ||
                    companyContactPerson.length === 0) {
                    throw new common_1.BadRequestException("Company participants are required and must be valid.");
                }
                companyContactPerson.forEach((id) => normalizedParticipants.push({
                    participantId: id,
                    participantCompanyId: companyId,
                    participantRecordType: constants_1.PARTICIPANT_TYPE.COMPANY_CONTACT,
                }));
            }
            if (participants.employeeParticipants) {
                const { employees } = participants.employeeParticipants;
                if (!Array.isArray(employees) || employees.length === 0) {
                    throw new common_1.BadRequestException("Employee participants are required and must be valid.");
                }
                employees.forEach((id) => normalizedParticipants.push({
                    participantId: id,
                    participantCompanyId: constants_1.PARTICIPANT_EMPLOYEE_COMPANY_ID,
                    participantRecordType: constants_1.PARTICIPANT_TYPE.EMPLOYEE,
                }));
            }
            // Fetch all existing participants for this meeting
            const existingParticipants = await manager.find(entities_1.MeetingParticipantMap, { where: { meetingId } });
            // Build a set for quick lookup
            const incomingParticipantsSet = new Set(normalizedParticipants.map((p) => `${p.participantId}_${p.participantRecordType}`));
            const existingParticipantsSet = new Set(existingParticipants.map((p) => `${p.participantId}_${p.participantRecordType}`));
            // Remove participants that exist in DB but not in the incoming payload
            const participantsToRemove = existingParticipants.filter((participant) => !incomingParticipantsSet.has(`${participant.participantId}_${participant.participantRecordType}`));
            if (participantsToRemove.length > 0) {
                await manager.remove(entities_1.MeetingParticipantMap, participantsToRemove);
            }
            // Add participants that are in the payload but not in the DB
            const participantsToAdd = normalizedParticipants.filter((participant) => !existingParticipantsSet.has(`${participant.participantId}_${participant.participantRecordType}`));
            for (const participant of participantsToAdd) {
                // Validate participant existence
                const participantType = participant.participantRecordType;
                const typeConfig = participantTypeMap[participantType];
                if (!typeConfig) {
                    throw new common_1.BadRequestException(`Invalid participant type: ${participantType}`);
                }
                if (participantType === constants_1.PARTICIPANT_TYPE.EMPLOYEE) {
                    await typeConfig.validate(manager, constants_1.PARTICIPANT_EMPLOYEE_COMPANY_ID, participant.participantId);
                }
                else {
                    await typeConfig.validate(manager, participant.participantCompanyId, participant.participantId);
                }
            }
            // New participants to add
            const newParticipants = participantsToAdd.map((participant) => ({
                meetingId,
                participantId: participant.participantId,
                participantCompanyId: participant.participantCompanyId,
                participantRecordType: participant.participantRecordType,
            }));
            if (newParticipants.length > 0) {
                await manager.save(entities_1.MeetingParticipantMap, newParticipants);
            }
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            if (error instanceof common_1.BadRequestException)
                throw error;
            throw new Error("Error saving meeting participants: " + error.message);
        }
    }
    // Save meeting documents
    async saveMeetingDocuments(meetingId, documents, manager) {
        try {
            // Validate all provided document IDs
            await Promise.all(documents.map(async (doc) => {
                if (doc.documentId) {
                    const document = await manager.findOne(entities_1.FileUpload, {
                        where: { id: doc.documentId },
                    });
                    if (!document) {
                        throw new common_1.NotFoundException(messages_1.errorMessages.documentWithIdNotFound(doc.documentId));
                    }
                }
                else {
                    throw new common_1.BadRequestException("Document ID is required for each document.");
                }
            }));
            // Fetch existing meeting documents
            const existingDocuments = await manager.find(entities_1.MeetingDocumentMap, {
                where: { meetingId },
            });
            const existingDocumentIds = existingDocuments.map((doc) => Number(doc.documentId));
            // Determine new documents to add
            const newDocuments = documents.filter((doc) => doc.documentId && !existingDocumentIds.includes(doc.documentId));
            if (newDocuments.length > 0) {
                const meetingDocuments = newDocuments.map((doc) => ({
                    meetingId,
                    documentId: doc.documentId,
                    documentTypeLid: doc.documentTypeLid,
                }));
                await manager.save(entities_1.MeetingDocumentMap, meetingDocuments);
            }
            // Determine documents to remove
            const documentsToRemove = existingDocuments.filter((doc) => !documents.some((d) => d.documentId === doc.documentId));
            if (documentsToRemove.length > 0) {
                await manager.remove(entities_1.MeetingDocumentMap, documentsToRemove);
            }
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            if (error instanceof common_1.BadRequestException)
                throw error;
            throw new Error("Error saving meeting documents: " + error.message);
        }
    }
    // Get a meeting by ID
    async getMeetingById(id) {
        try {
            const meeting = await this.meetingRepository.findOne({
                where: { id },
                relations: [
                    "participants",
                    "meetingDocs",
                    "company",
                    "opportunity",
                    "activity",
                ],
            });
            if (!meeting) {
                throw new common_1.NotFoundException(messages_1.errorMessages.meetingNotFound);
            }
            const lookUpValues = await this.entityService.getLookupValues([
                meeting.meetingTypeLid,
                meeting.meetingStatusLid,
                meeting.locationTypeLid,
            ]);
            if (!lookUpValues) {
                throw new Error("Failed to fetch lookup values");
            }
            // Map the lookup values to their respective fields
            const meetingType = lookUpValues.find((item) => item.id === meeting.meetingTypeLid);
            const meetingStatus = lookUpValues.find((item) => item.id === meeting.meetingStatusLid);
            const locationType = lookUpValues.find((item) => item.id === meeting.locationTypeLid);
            // Spread the lookup values into the meeting object
            const enrichedMeeting = {
                ...meeting,
                meetingType,
                meetingStatus,
                locationType,
            };
            // Validate and filter the response
            const filteredData = await this.transformMeetingResponse([
                enrichedMeeting,
            ]);
            return filteredData[0];
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error fetching meeting: " + error.message);
        }
    }
    // Delete a meeting by ID
    async deleteMeetingById(id) {
        try {
            const meeting = await this.meetingRepository.findOne({ where: { id } });
            if (!meeting) {
                throw new common_1.NotFoundException(messages_1.errorMessages.meetingNotFound);
            }
            await this.meetingRepository.softDelete({ id });
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error deleting meeting: " + error.message);
        }
    }
    // Get meetings with pagination, sorting, and searching
    async getMeetings(page, limit, searchArray, sort, userId, searchBy) {
        try {
            const { data, count } = await this.entityService.fetchEntityList(entities_1.Meeting, page, limit, sort.length > 0 ? sort : [{ field: "id", order: "ASC" }], ["company", "opportunity", "opportunity.policyType", "activity"], { createdBy: userId }, undefined, searchArray.length === 0 ? [] : searchArray, undefined, searchBy, constants_2.meetingSearchObject);
            if (!data || data.length === 0) {
                return { data: [], count: 0 };
            }
            // Extract all unique priorityLid values from the data
            const meetingTypeLids = [
                ...new Set(data.map((meeting) => meeting.meetingTypeLid).filter(Boolean)),
            ];
            const meetingStatusLids = [
                ...new Set(data.map((meeting) => meeting.meetingStatusLid).filter(Boolean)),
            ];
            const locationTypeLids = [
                ...new Set(data.map((meeting) => meeting.locationTypeLid).filter(Boolean)),
            ];
            const allLids = [
                ...new Set([
                    ...meetingTypeLids,
                    ...meetingStatusLids,
                    ...locationTypeLids,
                ]),
            ];
            // Fetch lookup values for all LIDs in one call
            const allLookupValues = await this.entityService.getLookupValues(allLids);
            const meetingTypeLookup = allLookupValues.filter((item) => meetingTypeLids.includes(item.id));
            const meetingStatusLookup = allLookupValues.filter((item) => meetingStatusLids.includes(item.id));
            const locationTypeLookup = allLookupValues.filter((item) => locationTypeLids.includes(item.id));
            return {
                data: await this.transformGetMeetingsResponse(data, meetingTypeLookup, meetingStatusLookup, locationTypeLookup),
                count,
            };
        }
        catch (error) {
            throw new Error("Failed to fetch get meetings list");
        }
    }
    async getMeetingsByOpportunityAndActivity(opportunityActivityId) {
        try {
            const { data, count } = await this.entityService.fetchEntityList(entities_1.Meeting, constants_1.DEFAULT_EMPLOYEE_PAGE, constants_1.DEFAULT_EMPLOYEE_PAGE_LIMIT, [{ field: "id", order: "ASC" }], [
                "company",
                "opportunity",
                "opportunity.policyType",
                "activity",
                "participants",
                "meetingDocs",
            ], { activityId: opportunityActivityId });
            if (!data || data.length === 0) {
                return [];
            }
            // Extract all unique priorityLid values from the data
            const meetingTypeLids = [
                ...new Set(data.map((meeting) => meeting.meetingTypeLid).filter(Boolean)),
            ];
            const meetingStatusLids = [
                ...new Set(data.map((meeting) => meeting.meetingStatusLid).filter(Boolean)),
            ];
            const locationTypeLids = [
                ...new Set(data.map((meeting) => meeting.locationTypeLid).filter(Boolean)),
            ];
            const allLids = [
                ...new Set([
                    ...meetingTypeLids,
                    ...meetingStatusLids,
                    ...locationTypeLids,
                ]),
            ];
            // Fetch lookup values for all LIDs in one call
            const allLookupValues = await this.entityService.getLookupValues(allLids);
            const meetingTypeLookup = allLookupValues.filter((item) => meetingTypeLids.includes(item.id));
            const meetingStatusLookup = allLookupValues.filter((item) => meetingStatusLids.includes(item.id));
            const locationTypeLookup = allLookupValues.filter((item) => locationTypeLids.includes(item.id));
            return await this.transformGetMeetingsResponse(data, meetingTypeLookup, meetingStatusLookup, locationTypeLookup);
        }
        catch (error) {
            throw new common_1.InternalServerErrorException(error instanceof Error
                ? error.message
                : "Failed to fetch meetings for the given opportunity and activity.");
        }
    }
    async transformMeetingResponse(meetings) {
        try {
            return meetings.map((meeting) => {
                const tpaParticipants = meeting.participants
                    ?.filter((p) => p.participantRecordType === constants_1.PARTICIPANT_TYPE.TPA_CONTACT)
                    .map((p) => p.participantId);
                const insurerParticipants = meeting.participants
                    ?.filter((p) => p.participantRecordType === constants_1.PARTICIPANT_TYPE.INSURER_CONTACT)
                    .map((p) => p.participantId);
                const companyParticipants = meeting.participants
                    ?.filter((p) => p.participantRecordType === constants_1.PARTICIPANT_TYPE.COMPANY_CONTACT)
                    .map((p) => p.participantId);
                const employeeParticipants = meeting.participants
                    ?.filter((p) => p.participantRecordType === constants_1.PARTICIPANT_TYPE.EMPLOYEE)
                    .map((p) => p.participantId);
                return {
                    ...this.omitFields(meeting),
                    companyId: undefined,
                    company: meeting.company
                        ? {
                            id: meeting.company.id,
                            name: meeting.company.companyName,
                            displayName: meeting.company.displayName,
                        }
                        : null,
                    opportunityId: undefined,
                    opportunity: meeting.opportunity
                        ? {
                            id: meeting.opportunity.opportunityId,
                            policy: meeting.opportunity.policyType?.lookUpValue,
                        }
                        : null,
                    activityId: undefined,
                    activity: meeting.activity
                        ? {
                            id: meeting.activity.id,
                            activityKey: meeting.activity.activityKey,
                            activityName: meeting.activity.activityName,
                        }
                        : null,
                    meetingTypeLid: undefined,
                    meetingStatusLid: undefined,
                    locationTypeLid: undefined,
                    meetingDocs: meeting.meetingDocs?.map((doc) => ({
                        documentId: doc.documentId,
                        documentTypeLid: doc.documentTypeLid,
                    })),
                    participants: undefined,
                    ...(tpaParticipants && tpaParticipants.length > 0
                        ? {
                            tpaParticipants: {
                                tpaId: meeting.participants.find((p) => p.participantRecordType === constants_1.PARTICIPANT_TYPE.TPA_CONTACT)?.participantCompanyId,
                                tpaContactPerson: tpaParticipants,
                            },
                        }
                        : {}),
                    ...(insurerParticipants && insurerParticipants.length > 0
                        ? {
                            insurerParticipants: {
                                insurerId: meeting.participants.find((p) => p.participantRecordType ===
                                    constants_1.PARTICIPANT_TYPE.INSURER_CONTACT)?.participantCompanyId,
                                insurerContactPerson: insurerParticipants,
                            },
                        }
                        : {}),
                    ...(companyParticipants && companyParticipants.length > 0
                        ? {
                            companyParticipants: {
                                companyId: meeting.participants.find((p) => p.participantRecordType ===
                                    constants_1.PARTICIPANT_TYPE.COMPANY_CONTACT)?.participantCompanyId,
                                companyContactPerson: companyParticipants,
                            },
                        }
                        : {}),
                    ...(employeeParticipants && employeeParticipants.length > 0
                        ? {
                            employeeParticipants: {
                                employees: employeeParticipants,
                            },
                        }
                        : {}),
                };
            });
        }
        catch (error) {
            throw new Error("Failed to transform meeting response.");
        }
    }
    async transformGetMeetingsResponse(meetings, meetingTypeLookup, meetingStatusLookup, locationTypeLookup) {
        try {
            return meetings.map((meeting) => ({
                ...this.omitFields(meeting),
                companyId: undefined,
                company: meeting.company
                    ? {
                        id: meeting.company.id,
                        name: meeting.company.companyName,
                        displayName: meeting.company.displayName,
                    }
                    : null,
                opportunityId: undefined,
                opportunity: meeting.opportunity
                    ? {
                        id: meeting.opportunity.opportunityId,
                        policy: meeting.opportunity.policyType?.lookUpValue,
                    }
                    : null,
                activityId: undefined,
                activity: meeting.activity
                    ? {
                        id: meeting.activity.id,
                        activityKey: meeting.activity.activityKey,
                        activityName: meeting.activity.activityName,
                    }
                    : null,
                meetingTypeLid: undefined,
                meetingType: {
                    id: meeting.meetingTypeLid,
                    lookUpValue: meetingTypeLookup?.find((item) => item.id === meeting.meetingTypeLid)?.lookUpValue ?? null,
                },
                meetingStatusLid: undefined,
                meetingStatus: {
                    id: meeting.meetingStatusLid,
                    lookUpValue: meetingStatusLookup?.find((item) => item.id === meeting.meetingStatusLid)?.lookUpValue ?? null,
                },
                locationTypeLid: undefined,
                locationType: {
                    id: meeting.locationTypeLid,
                    lookUpValue: locationTypeLookup?.find((item) => item.id === meeting.locationTypeLid)?.lookUpValue ?? null,
                },
            }));
        }
        catch (error) {
            throw new Error("Failed to transform get meetings response.");
        }
    }
    omitFields(entity) {
        if (!entity) {
            return {};
        }
        const { createdAt, updatedAt, deletedAt, createdBy, updatedBy, ...rest } = entity || {};
        return rest;
    }
    // Validate associations for meeting creation or update
    async validateMeetingAssociations(companyId, opportunityId, activityId) {
        try {
            if (companyId) {
                const company = await this.companyRepository.findOne({
                    where: { id: companyId },
                });
                if (!company) {
                    throw new common_1.NotFoundException(messages_1.errorMessages.companyNotFound);
                }
            }
            if (opportunityId) {
                const opportunity = await this.opportunityRepository.findOne({
                    where: { opportunityId },
                });
                if (!opportunity) {
                    throw new common_1.NotFoundException(messages_1.errorMessages.opportunityNotFound);
                }
            }
            if (opportunityId && activityId) {
                const activities = await this.opportunityActivityMapRepository.find({
                    where: { opportunityId },
                });
                if (!activities) {
                    throw new common_1.BadRequestException("No activities found for the given opportunity ID");
                }
                const activityExists = activities.some((activity) => activity.id === activityId);
                if (!activityExists) {
                    throw new common_1.BadRequestException(`Activity ID ${activityId} not found for the given opportunity ID`);
                }
            }
            else if (!opportunityId && activityId) {
                throw new common_1.BadRequestException("Activity ID provided without an associated Opportunity ID");
            }
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException) {
                throw error;
            }
            else if (error instanceof common_1.BadRequestException) {
                throw error;
            }
        }
    }
    // Validate meeting timings
    async validateMeetingTimings(meetingDate, startTime, endTime) {
        try {
            // Combine meetingDate and time strings into Date objects
            const startDateTime = new Date(`${meetingDate.toISOString().split("T")[0]}T${startTime}`);
            const endDateTime = new Date(`${meetingDate.toISOString().split("T")[0]}T${endTime}`);
            const now = new Date();
            if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
                throw new common_1.BadRequestException("Invalid start or end time format.");
            }
            if (startDateTime >= endDateTime) {
                throw new common_1.BadRequestException("Meeting start time must be before end time.");
            }
            if (startDateTime <= now || endDateTime <= now) {
                throw new common_1.BadRequestException("Meeting start and end times must be in the future.");
            }
        }
        catch (error) {
            if (error instanceof common_1.BadRequestException) {
                throw error;
            }
        }
    }
};
exports.MeetingRepository = MeetingRepository;
exports.MeetingRepository = MeetingRepository = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(entities_1.Meeting)),
    tslib_1.__param(1, (0, typeorm_1.InjectRepository)(entities_1.Company)),
    tslib_1.__param(2, (0, typeorm_1.InjectRepository)(entities_1.Opportunity)),
    tslib_1.__param(3, (0, typeorm_1.InjectRepository)(entities_1.OpportunityActivityMap)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _b : Object, typeof (_c = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _c : Object, typeof (_d = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _d : Object, typeof (_e = typeof entity_service_utils_1.EntityService !== "undefined" && entity_service_utils_1.EntityService) === "function" ? _e : Object])
], MeetingRepository);
